import { useState } from "react";
import { toast } from "sonner";
import { Plus, Download, CheckCircle2, XCircle, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Section, Empty, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import { useAuth, isAdmin } from "@/context/AuthContext";
import api, { errMsg, fmtDate, money, downloadFile } from "@/lib/api";

function PlanRow({ p, admin, onSaved }) {
  const [edit, setEdit] = useState(null);
  const save = async (patch) => {
    try { await api.put(`/billing/packages/${p.id}`, { name: p.name, tier: p.tier, price: p.price, duration_weeks: p.duration_weeks, active: p.active, ...patch }); setEdit(null); onSaved(); toast.success("Saved to CRM"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (edit) return (
    <div className="grid sm:grid-cols-5 gap-2 py-2.5 border-t border-[#1E293B]" data-testid={`plan-edit-${p.id}`}>
      <Input className="sm:col-span-2" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid={`plan-edit-name-${p.id}`} />
      <Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: Number(e.target.value) })} data-testid={`plan-edit-price-${p.id}`} />
      <Input type="number" value={edit.duration_weeks} onChange={(e) => setEdit({ ...edit, duration_weeks: Number(e.target.value) })} />
      <div className="flex gap-1"><Button size="sm" onClick={() => save(edit)} data-testid={`plan-edit-save-${p.id}`}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
    </div>
  );
  return (
    <div className="flex items-center justify-between py-2.5 border-t border-[#1E293B] text-sm" data-testid={`package-${p.id}`}>
      <div>{p.name} <span className="text-slate-500 text-xs ml-2">{p.tier} · {p.duration_weeks} weeks</span></div>
      <div className="flex items-center gap-4"><span className="font-mono-k">{money(p.price, p.currency)}</span>
        {admin && <Button size="sm" variant="ghost" onClick={() => setEdit({ name: p.name, price: p.price, duration_weeks: p.duration_weeks })} data-testid={`plan-edit-btn-${p.id}`}>Edit</Button>}
        <Switch checked={p.active} disabled={!admin} onCheckedChange={() => save({ active: !p.active })} data-testid={`package-active-${p.id}`} /></div>
    </div>
  );
}

function Packages() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [list, reload] = useLoad("/billing/packages");
  return (
    <Section title="Membership plans (CRM)" testid="packages-section">
      <p className="text-xs text-slate-500 mb-4">Membership plans are created in your CRM. Prices are in INR; members only see active plans.</p>
      {!list?.length ? <Empty text="No membership plans in the CRM." /> : list.map((p) => <PlanRow key={p.id} p={p} admin={admin} onSaved={reload} />)}
    </Section>
  );
}

function Coupons() {
  const [list, reload] = useLoad("/billing/coupons");
  const [f, setF] = useState({ code: "", kind: "percent", value: 10, max_uses: "", expires_at: "" });
  const add = async () => {
    try { await api.post("/billing/coupons", { ...f, max_uses: f.max_uses ? Number(f.max_uses) : null, expires_at: f.expires_at || null }); setF({ ...f, code: "" }); reload(); toast.success("Coupon created"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c) => { await api.put(`/billing/coupons/${c.id}`, { ...c, active: !c.active }); reload(); };
  return (
    <Section title="Coupon codes" testid="coupons-section">
      <div className="grid sm:grid-cols-6 gap-2 mb-5">
        <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="CODE" data-testid="coupon-code-input" />
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
          <SelectTrigger data-testid="coupon-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">% off</SelectItem><SelectItem value="fixed">Fixed off</SelectItem></SelectContent>
        </Select>
        <Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: Number(e.target.value) })} data-testid="coupon-value-input" />
        <Input type="number" value={f.max_uses} placeholder="Max uses" onChange={(e) => setF({ ...f, max_uses: e.target.value })} />
        <Input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} />
        <Button onClick={add} disabled={f.code.length < 3} data-testid="coupon-add-btn">Add</Button>
      </div>
      {!list?.length ? <Empty text="No coupons." /> : list.map((c) => (
        <div key={c.id} className="flex items-center justify-between py-2.5 border-t border-[#1E293B] text-sm" data-testid={`coupon-${c.code}`}>
          <div className="font-mono-k">{c.code} <span className="text-slate-400 ml-2">{c.kind === "percent" ? `${c.value}%` : c.value} off</span></div>
          <div className="flex items-center gap-4 text-xs text-slate-500">used {c.used}{c.max_uses ? `/${c.max_uses}` : ""}{c.expires_at && ` · exp ${c.expires_at}`}
            <Switch checked={c.active} onCheckedChange={() => toggle(c)} /></div>
        </div>
      ))}
    </Section>
  );
}

function Invoices() {
  const [status, setStatus] = useState("all");
  const [list, reload] = useLoad("/billing/invoices", status === "all" ? undefined : { status });
  const act = async (url, msg, body) => { try { await api.post(url, body); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section title="Invoices & payments" testid="invoices-section" action={
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-36" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>{["all", "scheduled", "open", "paid", "failed", "void"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>}>
      {!list?.length ? <Empty text="No invoices." /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="eyebrow text-left"><th className="py-2">Invoice</th><th>Client</th><th>Package</th><th>Total</th><th>Status</th><th /></tr></thead>
          <tbody>{list.map((i) => (
            <tr key={i.id} className="border-t border-[#1E293B]" data-testid={`invoice-row-${i.id}`}>
              <td className="py-3 font-mono-k text-xs">{i.number}<div className="text-slate-500">{fmtDate(i.created_at)}</div></td>
              <td>{i.client_name}</td>
              <td className="text-slate-400">{i.package_name} <span className="text-xs">({i.kind})</span>{i.installment_count > 1 && <div className="text-xs">Installment {i.installment_number}/{i.installment_count} · due {fmtDate(i.due_date)}</div>}{i.coupon_code && <div className="text-xs text-emerald-400">{i.coupon_code} −{money(i.discount, i.currency)}</div>}</td>
              <td className="font-mono-k">{money(i.total, i.currency)}</td>
              <td><StatusBadge status={i.status} />{i.status === "failed" && <div className="text-xs text-red-400 mt-1">{i.failure_reason} · {i.attempts}×</div>}</td>
              <td className="text-right whitespace-nowrap">
                {["open", "failed"].includes(i.status) && <>
                  <Button size="sm" variant="ghost" onClick={() => act(`/billing/invoices/${i.id}/mark-paid`, "Marked paid - membership extended")} data-testid={`invoice-mark-paid-${i.id}`}><CheckCircle2 size={14} className="mr-1" />Paid offline</Button>
                  {i.status === "open" && <Button size="sm" variant="ghost" onClick={() => act(`/billing/invoices/${i.id}/mark-failed`, "Marked failed", { reason: "Marked failed by coach" })} data-testid={`invoice-mark-failed-${i.id}`}><XCircle size={14} /></Button>}
                  <Button size="sm" variant="ghost" onClick={() => act(`/billing/invoices/${i.id}/void`, "Invoice voided")} data-testid={`invoice-void-${i.id}`}><Ban size={14} /></Button>
                </>}
                <Button size="icon" variant="ghost" onClick={() => downloadFile(`/billing/invoices/${i.id}/pdf`, `${i.number}.pdf`)} data-testid={`invoice-pdf-${i.id}`}><Download size={14} /></Button>
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Section>
  );
}

export default function Billing() {
  const [prov] = useLoad("/billing/providers");
  return (
    <div>
      <PageHeader eyebrow="Revenue ops" title="Billing" testid="billing-title">
        {prov && ["stripe", "cashfree"].map((p) => (
          <span key={p} data-testid={`provider-status-${p}`} className={`text-xs font-mono-k uppercase px-3 py-1.5 rounded-full border ${prov[p] ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
            {p}: {prov[p] ? "connected" : "keys needed"}
          </span>
        ))}
      </PageHeader>
      <Tabs defaultValue="invoices">
        <TabsList className="bg-[#111827] border border-[#1E293B] mb-6">
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="packages" data-testid="billing-tab-packages">Membership plans</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="billing-tab-coupons">Coupons</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices"><Invoices /></TabsContent>
        <TabsContent value="packages"><Packages /></TabsContent>
        <TabsContent value="coupons"><Coupons /></TabsContent>
      </Tabs>
      <Label className="block mt-6 text-xs text-slate-500">Failed card/UPI payments are flagged automatically; clients can retry from their Membership page, or you can record an offline payment.</Label>
    </div>
  );
}
